"use client";

import { Container, Typography, Box, Button, Paper, Alert, useTheme } from "@mui/material";
import Link from "next/link";
import Image from "next/image";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AssessmentIcon from "@mui/icons-material/Assessment";
import GroupsIcon from "@mui/icons-material/Groups";
import InsightsIcon from "@mui/icons-material/Insights";
import EnrollmentHeader from "@/components/EnrollmentHeader";
import EnrollmentFooter from "@/components/EnrollmentFooter";
import { useStudyStatus } from "@/hooks/useStudyStatus";
import { useMockDate } from "@/hooks/useMockDate";
import { usePageTitle } from "@/hooks/usePageTitle";
import IndicationOfInterestForm from "@/components/IndicationOfInterestForm";

export default function CadaLandingPage() {
    const theme = useTheme();
    const tenant = "cada";

    usePageTitle("Workforce Study Enrollment", tenant);
    const mockDate = useMockDate();
    const { status, isEnrollmentOpen, submissionStartDate, statusMessage, studyYear, loading } = useStudyStatus(
        tenant,
        mockDate
    );

    return (
        <Box sx={{ bgcolor: "white", minHeight: "100vh" }}>
            <EnrollmentHeader tenant={tenant} />

            <Container maxWidth="lg" sx={{ py: 4 }} component="main" role="main">
                {!loading && status && (
                    <Alert
                        severity={
                            status === "EnrollmentNotYetOpen"
                                ? "info"
                                : status === "EnrollmentOpen"
                                  ? "success"
                                  : status === "EnrollmentExtension"
                                    ? "warning"
                                    : status === "DataSubmissionOnly"
                                      ? "warning"
                                      : status === "StudyClosed"
                                        ? "error"
                                        : "info"
                        }
                        sx={{ mb: 3 }}
                        role="alert"
                        aria-live="polite"
                    >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {statusMessage || `Enrollment is open! Data submission begins ${submissionStartDate}`}
                        </Typography>
                    </Alert>
                )}

                {/* Hero Section with CTA */}
                <Paper
                    elevation={2}
                    sx={{
                        p: 4,
                        mb: 3,
                        textAlign: "center",
                        bgcolor: theme.palette.primary.main,
                        color: "white",
                    }}
                    component="section"
                    aria-labelledby="hero-title"
                >
                    <Typography
                        id="hero-title"
                        variant="h4"
                        component="h1"
                        sx={{ mb: 2, fontWeight: 700 }}
                    >
                        Welcome to the CADA Dealership Workforce Study
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.95 }}>
                        Participate in the industry&apos;s leading workforce research study and gain
                        valuable insights into compensation, benefits, and retention trends.
                    </Typography>
                    <Button
                        component={Link}
                        href="/portal"
                        variant="contained"
                        size="large"
                        disabled={!isEnrollmentOpen || loading}
                        aria-label={
                            isEnrollmentOpen
                                ? "Go to dealer portal"
                                : "Enrollment is currently closed"
                        }
                        sx={{
                            bgcolor: "white",
                            color: theme.palette.primary.main,
                            px: 6,
                            py: 1.5,
                            fontSize: "1.1rem",
                            fontWeight: 600,
                            "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.9)",
                            },
                            "&.Mui-disabled": {
                                bgcolor: "rgba(255, 255, 255, 0.5)",
                                color: "rgba(0, 0, 0, 0.4)",
                            },
                        }}
                    >
                        {loading
                            ? "Loading..."
                            : isEnrollmentOpen
                              ? "Enroll Now"
                              : "Enrollment Closed"}
                    </Button>
                </Paper>

                {/* Indication of Interest Form - Show when enrollment not yet open */}
                {status === "EnrollmentNotYetOpen" && (
                    <Box sx={{ mb: 3 }}>
                        <IndicationOfInterestForm tenant={tenant} studyYear={studyYear} />
                    </Box>
                )}

                {/* Benefits Section - Compact Grid */}
                <Paper
                    elevation={2}
                    sx={{ p: 3, mb: 3 }}
                    component="section"
                    aria-labelledby="benefits-title"
                >
                    <Typography
                        id="benefits-title"
                        variant="h5"
                        component="h2"
                        sx={{ mb: 2, fontWeight: 700 }}
                    >
                        Why Participate?
                    </Typography>
                    <Box
                        role="list"
                        aria-label="Key benefits"
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                            gap: 2,
                        }}
                    >
                        <Box
                            role="listitem"
                            sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                        >
                            <DashboardIcon
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: 28,
                                    flexShrink: 0,
                                }}
                                aria-hidden="true"
                            />
                            <Box>
                                <Typography variant="subtitle2">
                                    Make comparisons to peer groups
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Gain insights into retention strategies
                                </Typography>
                            </Box>
                        </Box>
                        <Box
                            role="listitem"
                            sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                        >
                            <AssessmentIcon
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: 24,
                                    flexShrink: 0,
                                }}
                                aria-hidden="true"
                            />
                            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                Make informed decisions on pay plans
                            </Typography>
                        </Box>
                        <Box
                            role="listitem"
                            sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                        >
                            <GroupsIcon
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: 24,
                                    flexShrink: 0,
                                }}
                                aria-hidden="true"
                            />
                            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                Reduce turnover and retain top employees
                            </Typography>
                        </Box>
                        <Box
                            role="listitem"
                            sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                        >
                            <InsightsIcon
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: 24,
                                    flexShrink: 0,
                                }}
                                aria-hidden="true"
                            />
                            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                Understand demographic challenges
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {/* How It Works */}
                <Paper
                    elevation={2}
                    sx={{ p: 3, mb: 3 }}
                    component="section"
                    aria-labelledby="how-it-works-title"
                >
                    <Typography
                        id="how-it-works-title"
                        variant="h6"
                        component="h2"
                        sx={{ mb: 1.5, fontWeight: 600 }}
                    >
                        How It Works
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Once enrolled, you&apos;ll receive an email with instructions to complete an
                        online benefits questionnaire and payroll file. All responses and data are
                        confidential.
                    </Typography>
                </Paper>

                {/* Deadline Alert */}
                <Alert
                    severity="info"
                    icon={false}
                    sx={{ textAlign: "center" }}
                    role="alert"
                    aria-live="polite"
                >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Enrollment closes March 2026.
                    </Typography>
                </Alert>

                {/* CADA-Specific Sections */}
                {/* Private & Confidential */}
                <Box sx={{ textAlign: "center", my: 6 }}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{ mb: 2, fontWeight: 700, color: "#c8102e" }}
                    >
                        PRIVATE &amp; CONFIDENTIAL
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        All individual dealership payroll information will remain protected and
                        confidential. All data will be aggregated and averaged using a minimum of
                        seven unrelated dealerships and ten payroll records.
                    </Typography>
                </Box>

                {/* About Us Section with Image */}
                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        mb: 3,
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: "flex-start",
                        gap: 3,
                    }}
                    component="section"
                    id="AboutUS"
                    aria-labelledby="about-us-title"
                >
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            id="about-us-title"
                            variant="h5"
                            component="h2"
                            sx={{ mb: 2, fontWeight: 700 }}
                        >
                            ABOUT US
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            The Canadian Automobile Dealers Association (CADA) is the national
                            association representing new car and truck dealers. Acting as the voice
                            of the dealers at the national level, CADA serves as an advocate to
                            government, industry and the public.
                            <br />
                            <br />
                            There are over 3200 franchised automobile and truck dealerships that
                            sell new cars and trucks in Canada. These dealers collectively employ
                            over 140,000 people across the country and represent a key sector of
                            Canada&apos;s economy. Through Canadian dealers, CADA is represented in
                            nearly every community in Canada.
                        </Typography>
                    </Box>
                    <Box sx={{ flexShrink: 0, width: { xs: "100%", md: "400px" } }}>
                        <Image
                            src="/iStock-640042252.png"
                            alt="CADA office"
                            width={400}
                            height={280}
                            style={{ width: "100%", height: "auto", borderRadius: "8px" }}
                        />
                    </Box>
                </Paper>

                {/* Provincial and Regional Associations */}
                <Box
                    sx={{ textAlign: "center", my: 6 }}
                    component="section"
                    id="Associations"
                    aria-labelledby="associations-title"
                >
                    <Typography
                        id="associations-title"
                        variant="h5"
                        component="h2"
                        sx={{ mb: 3, fontWeight: 700, color: "#c8102e" }}
                    >
                        PROVINCIAL AND REGIONAL ASSOCIATIONS
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            gap: 2,
                            mb: 3,
                        }}
                    >
                        <Image
                            src="/MDA.png"
                            alt="MDA"
                            width={100}
                            height={60}
                            style={{ objectFit: "contain" }}
                        />
                        <Image
                            src="/NCDA.png"
                            alt="NCDA"
                            width={150}
                            height={60}
                            style={{ objectFit: "contain" }}
                        />
                        <Image
                            src="/CCAM.png"
                            alt="CCAM"
                            width={225}
                            height={60}
                            style={{ objectFit: "contain" }}
                        />
                        <Image
                            src="/CCAQ.png"
                            alt="CCAQ"
                            width={175}
                            height={60}
                            style={{ objectFit: "contain" }}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            gap: 2,
                            mb: 3,
                        }}
                    >
                        <Image
                            src="/MMDA.png"
                            alt="MMDA"
                            width={125}
                            height={60}
                            style={{ objectFit: "contain" }}
                        />
                        <Image
                            src="/NBADA.png"
                            alt="NBADA"
                            width={150}
                            height={60}
                            style={{ objectFit: "contain" }}
                        />
                        <Image
                            src="/ADANL.png"
                            alt="ADANL"
                            width={150}
                            height={60}
                            style={{ objectFit: "contain" }}
                        />
                        <Image
                            src="/NSADA.png"
                            alt="NSADA"
                            width={200}
                            height={60}
                            style={{ objectFit: "contain" }}
                        />
                    </Box>
                    <Box
                        sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 2 }}
                    >
                        <Image
                            src="/MVRO.png"
                            alt="MVRO"
                            width={200}
                            height={60}
                            style={{ objectFit: "contain" }}
                        />
                        <Image
                            src="/PEI.png"
                            alt="PEI"
                            width={200}
                            height={60}
                            style={{ objectFit: "contain" }}
                        />
                        <Image
                            src="/SADA.png"
                            alt="SADA"
                            width={125}
                            height={60}
                            style={{ objectFit: "contain" }}
                        />
                    </Box>
                </Box>

                {/* Decorative Image */}
                <Box sx={{ width: "100%", my: 4 }}>
                    <Image
                        src="/iStock-1035128118.png"
                        alt=""
                        width={1200}
                        height={300}
                        style={{ width: "100%", height: "auto" }}
                        aria-hidden="true"
                    />
                </Box>

                {/* Sponsor Section */}
                <Box
                    sx={{ textAlign: "center", my: 6 }}
                    component="section"
                    id="Sponsors"
                    aria-labelledby="sponsor-title"
                >
                    <Typography
                        id="sponsor-title"
                        variant="h5"
                        component="h2"
                        sx={{ mb: 3, fontWeight: 700, color: "#c8102e" }}
                    >
                        THANK YOU TO OUR SPONSOR
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Image
                            src="/MNP_logo343C.svg"
                            alt="MNP - Our Sponsor"
                            width={500}
                            height={160}
                            style={{ objectFit: "contain" }}
                        />
                    </Box>
                </Box>

                <EnrollmentFooter tenant={tenant} />
            </Container>
        </Box>
    );
}
